import { convertToMMDDYYYY } from 'helper/utility';
import * as yup from 'yup';

import {
    GOVT_ID_API_IMAGE_KEY_MAP,
    GOVT_ID_API_WATERMARK_IMAGE_KEY_MAP,
} from './components/constants';
import { TWO_FA_NUDGE_STATES, TWO_FA_NUDGE_STATUES } from './constant';

export const initialMvrFormValues = (data) => {
    return {
        licenseNum: data?.id_number ?? '',
        issuedState: data?.state ?? '',
        issuedDate: convertToMMDDYYYY(data?.id_issued_date) ?? '',
        expiryDate: convertToMMDDYYYY(data?.id_expiration_date) ?? '',
        classCode: '',
        classDescription: '',
        prevLicenseNum: '',
    };
};

export const mvrFormValidationSchema = yup.object().shape({
    licenseNum: yup.string().required('Enter license number'),
    issuedState: yup.string().required('Enter state code'),
    issuedDate: yup.string().required('Enter issued date'),
    expiryDate: yup.string().required('Enter expiry date'),
    classCode: yup.string(),
    classDescription: yup.string(),
    prevLicenseNum: yup.string(),
});

export const modifyImageDataInGovtIdResponse = (data) => {
    if (data) {
        const { watermarkedImages, ...restData } = data;
        return {
            ...restData,
            [GOVT_ID_API_IMAGE_KEY_MAP.ID_FRONT]:
                watermarkedImages[GOVT_ID_API_WATERMARK_IMAGE_KEY_MAP.ID_FRONT] ?? null,
            [GOVT_ID_API_IMAGE_KEY_MAP.ID_BACK]:
                watermarkedImages[GOVT_ID_API_WATERMARK_IMAGE_KEY_MAP.ID_BACK] ?? null,
            [GOVT_ID_API_IMAGE_KEY_MAP.FACE]:
                watermarkedImages[GOVT_ID_API_WATERMARK_IMAGE_KEY_MAP.FACE] ?? null,
            [GOVT_ID_API_IMAGE_KEY_MAP.SIGNATURE]:
                watermarkedImages[GOVT_ID_API_WATERMARK_IMAGE_KEY_MAP.SIGNATURE] ?? null,
            [GOVT_ID_API_IMAGE_KEY_MAP.SELFIE]:
                watermarkedImages[GOVT_ID_API_WATERMARK_IMAGE_KEY_MAP.SELFIE] ?? null,
            AuthenticationResultSummary: null,
            ResponseCodeReformatImage: null,
            ResponseCodePassportFaceImage: null,
            Issued_NonMRZ: null,
            PersonalNumber_NonMRZ: null,
        };
    }
    return data;
};

// NOTE: GWC records does not have all the fields mention here but necessary, there can be more field for particular user
export const givenGWCRecords = (data) => ({
    score: data?.score,
    fullName: data?.fullName,
    title: data?.title,
    firstName: data?.name?.firstName,
    middleName: data?.name?.middleName,
    lastName: data?.name?.lastName,
    nameSuffix: data?.name?.nameSuffix,
    officialTitle: data?.officialTitle,
    eyes: data?.eyes,
    hair: data?.hair,
    weight: data?.weight,
    height: data?.height,
    marks: data?.marks,
    sex: data?.sex,
    languages: data?.languages,
    TIN: data?.tin,
    documents: data?.documents,
    placeOfBirth: data?.placeOfBirth,
    dateOfBirth: data?.dateOfBirth,
    citizenship: data?.citizenship,
    phone: data?.phone,
    fax: data?.fax,
    emailAddress: data?.emailAddress,
    website: data?.website,
    constituancy: data?.constituancy,
    politicalParty: data?.politicalParty,
    imageURL: data?.imageUrl,
    pageURL: data?.pageUrl,
    source: data?.source,
    type: data?.type,
    offense: data?.offense,
    wantedBy: data?.wantedBy,
    program: data?.program,
    legalBasis: data?.legalBasis,
    listingDate: data?.listingDate?.year
        ? `${data?.listingDate.month}-${data.listingDate?.day}-${data?.listingDate?.year}`
        : null,
    callSign: data?.callSign,
    vesselType: data?.vesselType,
    tonnage: data?.tonnage,
    GRT: data?.grt,
    vesselFlag: data?.vesselFlag,
    vesselOwner: data?.vesselOwner,
    remarks: data?.remarks,
    address: data?.address
        ? `${data.address?.line1} ${data.address?.city} ${data.address?.state} ${data.address?.zip}`
        : null,
    country: data?.country,
    addressRemarks: data?.addressRemarks,
    aliasType: data?.aliasType,
    aliasName: data?.aliasName,
});

export const formatGWCRecords = (data) => {
    return Object.fromEntries(
        Object.entries(givenGWCRecords(data)).filter(([_, value]) => value || value === '')
    );
};

export const getMissingContactNudgeState = (isDummyEmail, isPhoneNumberAvailable) => {
    if (isDummyEmail && !isPhoneNumberAvailable) return TWO_FA_NUDGE_STATES.MISSING_EMAIL_AND_PHONE;
    if (!isPhoneNumberAvailable) return TWO_FA_NUDGE_STATES.MISSING_PHONE;
    if (isDummyEmail) return TWO_FA_NUDGE_STATES.MISSING_EMAIL;
    return null;
};

export const getTwoFAnudgeConfiguration = (verifiedPhone, verifiedEmail) => {
    const nudges = [];
    const isPhoneFailed = verifiedPhone === false;
    const isEmailFailed = verifiedEmail === false;
    const isPhoneVerified = verifiedPhone === true;
    const isEmailVerified = verifiedEmail === true;

    const addNudge = (state, status) => {
        nudges.push({ state, status });
    };

    // if both passed
    if (isPhoneVerified && isEmailVerified) {
        addNudge(TWO_FA_NUDGE_STATES.EMAIL_AND_PHONE_SUCCESS, TWO_FA_NUDGE_STATUES.SUCCESS);
        // if both failed
    } else if (isPhoneFailed && isEmailFailed) {
        addNudge(TWO_FA_NUDGE_STATES.EMAIL_AND_PHONE_FAIL, TWO_FA_NUDGE_STATUES.FAIL);
    } else {
        // if only phone verification passed
        if (isPhoneVerified) {
            addNudge(TWO_FA_NUDGE_STATES.PHONE_SUCCESS, TWO_FA_NUDGE_STATUES.SUCCESS);
            // if phone failed
        } else if (isPhoneFailed) {
            addNudge(TWO_FA_NUDGE_STATES.PHONE_FAIL, TWO_FA_NUDGE_STATUES.WARNING);
        }
        // if email passed
        if (isEmailVerified) {
            addNudge(TWO_FA_NUDGE_STATES.EMAIL_SUCCESS, TWO_FA_NUDGE_STATUES.SUCCESS);
            // if email failed
        } else if (isEmailFailed) {
            addNudge(TWO_FA_NUDGE_STATES.EMAIL_FAIL, TWO_FA_NUDGE_STATUES.WARNING);
        }
    }

    return nudges;
};

export const WORKFLOW_STATUS_CODES = {
    PENDING: 'PENDING',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
    SUCCESS: 'SUCCESS',
};
